import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { FontProvider } from "./contexts/FontContext";
import Layout from "./components/Layout";
import PrivateRoute from "./components/PrivateRoute";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/student/Dashboard";
import Videos from "./pages/student/Videos";
import VideoPlayer from "./pages/student/VideoPlayer";
import Progress from "./pages/student/Progress";
import Certificates from "./pages/student/Certificates";
import Profile from "./pages/student/Profile";
import StudentChat from "./pages/student/Chat";
import FolderVideos from "./pages/student/FolderVideos";
import Settings from "./pages/Settings";
import LandingPage from "./pages/LandingPage";

// Admin Pages
import AdminUsers from "./pages/admin/Users";
import AdminVideos from "./pages/admin/Video/Videos";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminActivity from "./pages/admin/ActivityLogs";
import AdminProfile from "./pages/admin/Profile";
import CertificateTemplates from "./pages/admin/CertificateTemplates";
import AdminChat from "./pages/admin/Chat";
import Categories from "./pages/admin/Categories";
import AdminLandingPage from "./pages/admin/LandingPage";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FontProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Student Routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/videos"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <Videos />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/videos/:id"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <VideoPlayer />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/folder/:id"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <FolderVideos />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <Progress />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/certificates"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <Certificates />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <PrivateRoute studentOnly>
                    <Layout>
                      <StudentChat />
                    </Layout>
                  </PrivateRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminUsers />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/videos"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminVideos />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminAnalytics />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/activity"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminActivity />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/certificates"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <CertificateTemplates />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/chat"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminChat />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <Categories />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/landing-page"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminLandingPage />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/profile"
                element={
                  <PrivateRoute adminOnly>
                    <Layout>
                      <AdminProfile />
                    </Layout>
                  </PrivateRoute>
                }
              />

              {/* Shared Routes */}
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Profile />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </PrivateRoute>
                }
              />

              {/* Default Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </FontProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
